// frontend/src/pages/RPSCalculator.js
// Page publique de calcul du score RPS — MVP 3 Split
// Design Aurora dark : formulaire à gauche + preview du rapport à droite (preuve de valeur anticipée)
// Migré en thème Aurora (Vague 4) — logique JS inchangée.

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuroraBackground from '../components/AuroraBackground/AuroraBackground';
import AuroraNavbar from '../components/AuroraNavbar/AuroraNavbar';
import Footer from '../components/Footer';
import MetaHead from '../components/MetaHead';
import { API_BASE } from '../config/api';
import '../styles/pg-rps.css';

export default function RPSCalculator() {
  const { t } = useTranslation('scoreRps');

  // ============================================================================
  // États du formulaire (inchangés)
  // ============================================================================
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  // État global : 'idle' | 'calculating' | 'result' | 'error'
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // ============================================================================
  // Recherche Places (inchangée)
  // ============================================================================
  const handleSearch = async (e) => {
    e.preventDefault();
    if (query.trim().length < 3) {
      setError(t('error_min_chars'));
      return;
    }

    setError(null);
    setSearching(true);
    setSearchResults([]);

    try {
      const res = await fetch(`${API_BASE}/api/public/rps/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });

      // Parse défensif : un 502/504 Railway peut renvoyer du HTML → res.json() throw.
      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('RPSCalculator.handleSearch: réponse non-JSON', parseErr, 'status:', res.status);
      }

      if (!res.ok) {
        console.error('RPSCalculator.handleSearch: HTTP non-OK', { status: res.status, data });
        throw new Error(data.error || `Erreur de recherche (HTTP ${res.status})`);
      }

      const results = Array.isArray(data.results) ? data.results : [];
      if (results.length === 0) {
        setError(t('error_no_results'));
      } else {
        setSearchResults(results);
      }
    } catch (err) {
      console.error('RPSCalculator.handleSearch: exception', err);
      setError(err.message || 'Erreur de recherche');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectPlace = (place) => {
    setSelectedPlace(place);
    setSearchResults([]);
    setQuery(place.name);
  };

  // ============================================================================
  // Calcul RPS (inchangé)
  // ============================================================================
  const handleCalculate = async (e) => {
    e.preventDefault();
    if (!selectedPlace) {
      setError(t('error_no_selection'));
      return;
    }
    if (!email || !email.includes('@')) {
      setError('Email invalide');
      return;
    }

    setError(null);
    setStatus('calculating');

    try {
      const res = await fetch(`${API_BASE}/api/public/rps/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          place_id: selectedPlace.place_id,
          email,
          name: name || undefined
        })
      });

      // Parse défensif — Railway cold start peut renvoyer 504/HTML, fait planter res.json().
      let data = {};
      try {
        data = await res.json();
      } catch (parseErr) {
        console.error('RPSCalculator.handleCalculate: réponse non-JSON', parseErr, 'status:', res.status);
      }

      if (!res.ok) {
        console.error('RPSCalculator.handleCalculate: HTTP non-OK', { status: res.status, data });
        throw new Error(data.error || `Erreur de calcul (HTTP ${res.status}). Réessayez.`);
      }

      // Validation de la shape AVANT setState : sans ça, PreviewResult crash au render
      // sur scores.total.value (useEffect dep), entraînant une page blanche silencieuse.
      if (!data.scores?.total || typeof data.scores.total.value !== 'number'
          || !data.verdict || !data.restaurant) {
        console.error('RPSCalculator.handleCalculate: shape de réponse invalide', data);
        throw new Error(t('error_incomplete'));
      }

      setResult(data);
      setStatus('result');

      // Scroll vers le résultat sur mobile (la preview est en dessous)
      if (window.innerWidth < 900) {
        setTimeout(() => {
          document.getElementById('rps-preview')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } catch (err) {
      console.error('RPSCalculator.handleCalculate: exception', err);
      setError(err.message || 'Erreur de connexion. Vérifiez votre réseau et réessayez.');
      setStatus('error');
    }
  };

  // ============================================================================
  // Rendu Aurora
  // ============================================================================
  return (
    <>
      <MetaHead page="scoreRps" />
      <AuroraBackground />
      <AuroraNavbar />

      <main className="pg-rps">
        <div className="pg-rps-split">

          {/* ════════════════════════════════════════════ */}
          {/* COLONNE GAUCHE — Formulaire                  */}
          {/* ════════════════════════════════════════════ */}
          <div className="pg-rps-col-left">
            <div className="pg-rps-badge-top">{t('badge_free')}</div>

            <h1 className="pg-rps-h1">
              {t('h1_line1')}<br />
              {t('h1_line2')}{' '}
              <span className="pg-rps-h1-highlight">{t('h1_highlight')}</span>
            </h1>

            <p className="pg-rps-subtitle">
              {t('subtitle')}
            </p>

            {/* ─── Étape 1 — Recherche restaurant ─── */}
            <div className="pg-rps-step-card">
              <div className="pg-rps-step-label">{t('step1_label')}</div>
              <form onSubmit={handleSearch} className="pg-rps-input-row">
                <input
                  type="text"
                  placeholder={t('placeholder_search')}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (selectedPlace) setSelectedPlace(null);
                  }}
                  className="pg-rps-input"
                />
                <button
                  type="submit"
                  disabled={searching}
                  className="pg-rps-btn-search"
                >
                  {searching ? '...' : '🔍'}
                </button>
              </form>

              {searchResults.length > 0 && !selectedPlace && (
                <div className="pg-rps-results-list">
                  {searchResults.map((place) => (
                    <button
                      key={place.place_id}
                      onClick={() => handleSelectPlace(place)}
                      className="pg-rps-result-item"
                      type="button"
                    >
                      <div className="pg-rps-result-name">{place.name}</div>
                      <div className="pg-rps-result-address">{place.address}</div>
                      <div className="pg-rps-result-meta">
                        {place.rating ? `⭐ ${place.rating} · ${place.reviews_count} avis` : t('rating_none')}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {selectedPlace && (
                <div className="pg-rps-selected-badge">
                  {t('selected_badge', { name: selectedPlace.name })}
                  <button onClick={() => { setSelectedPlace(null); setQuery(''); }} className="pg-rps-change-btn" type="button">
                    {t('btn_change')}
                  </button>
                </div>
              )}
            </div>

            {/* ─── Étape 2 — Email ─── */}
            <div className={`pg-rps-step-card${selectedPlace ? '' : ' is-disabled'}`}>
              <div className="pg-rps-step-label">{t('step2_label')}</div>
              <input
                type="text"
                placeholder={t('placeholder_name')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pg-rps-input"
                style={{ marginBottom: 8 }}
              />
              <input
                type="email"
                placeholder={t('placeholder_email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pg-rps-input"
              />
            </div>

            {/* ─── CTA principal ─── */}
            <button
              onClick={handleCalculate}
              disabled={!selectedPlace || !email || status === 'calculating'}
              className="pg-rps-btn-primary"
              type="button"
            >
              {status === 'calculating'
                ? t('btn_calculate_busy')
                : t('btn_calculate_idle')}
            </button>

            {error && <div className="pg-rps-error">{error}</div>}

            <div className="pg-rps-trust-bar">
              <span>{t('trust_privacy')}</span>
              <span>{t('trust_speed')}</span>
              <span>{t('trust_free')}</span>
            </div>
          </div>

          {/* ════════════════════════════════════════════ */}
          {/* COLONNE DROITE — Preview du rapport         */}
          {/* ════════════════════════════════════════════ */}
          <div id="rps-preview" className="pg-rps-col-right">
            {status === 'idle' && <PreviewDemo />}
            {status === 'calculating' && <PreviewLoading />}
            {status === 'result' && result && <PreviewResult result={result} />}
            {status === 'error' && <PreviewDemo />}
          </div>

        </div>
      </main>

      <Footer />
    </>
  );
}

// ============================================================================
// Preview — État IDLE (exemple alléchant, avant calcul)
// ============================================================================
function PreviewDemo() {
  const { t } = useTranslation('scoreRps');
  return (
    <div className="pg-rps-preview-card">
      <div className="pg-rps-preview-badge">{t('preview_demo_badge')}</div>
      <div className="pg-rps-preview-label">{t('preview_demo_label')}</div>
      <div className="pg-rps-score" style={{ color: '#FBBF24' }}>
        67<span className="pg-rps-score-max">/100</span>
      </div>
      <div className="pg-rps-verdict-badge" style={{ background: 'rgba(251,191,36,0.15)', color: '#FBBF24' }}>
        {t('preview_demo_verdict')}
      </div>

      <div className="pg-rps-quote">
        "{t('preview_demo_quote')}"
        <div className="pg-rps-quote-source">{t('preview_demo_quote_source')}</div>
      </div>

      <PreviewBar label={t('bar_label_note')} value={30} max={40} detail="4,0/5" />
      <PreviewBar label={t('bar_label_satisfaction')} value={18} max={30} detail={t('bar_detail_satisfaction')} />
      <PreviewBar label={t('bar_label_volume')} value={19} max={30} detail="87 avis" />

      <div className="pg-rps-preview-footer">
        <div className="pg-rps-preview-footer-title">{t('preview_demo_footer_title')}</div>
        <div className="pg-rps-preview-footer-item">{t('preview_demo_footer_item1')}</div>
        <div className="pg-rps-preview-footer-item">{t('preview_demo_footer_item2')}</div>
        <div className="pg-rps-preview-footer-item">{t('preview_demo_footer_item3')}</div>
      </div>
    </div>
  );
}

// ============================================================================
// Preview — État CALCULATING (skeleton animé)
// ============================================================================
function PreviewLoading() {
  const { t } = useTranslation('scoreRps');
  const [dots, setDots] = useState('');
  useEffect(() => {
    const int = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 400);
    return () => clearInterval(int);
  }, []);

  return (
    <div className="pg-rps-preview-card">
      <div className="pg-rps-preview-badge is-active">{t('preview_loading_badge')}</div>
      <div className="pg-rps-preview-label">{t('preview_loading_label')}{dots}</div>
      <div className="pg-rps-score-placeholder">
        ··<span className="pg-rps-score-max">/100</span>
      </div>
      <div className="pg-rps-loading-steps">
        <div className="pg-rps-loading-step is-done">
          <span className="pg-rps-check">✓</span>
          {t('preview_loading_step1')}
        </div>
        <div className="pg-rps-loading-step is-done">
          <span className="pg-rps-check">✓</span>
          {t('preview_loading_step2')}
        </div>
        <div className="pg-rps-loading-step is-active">
          <span className="pg-rps-spin">○</span>
          {t('preview_loading_step3')}
        </div>
        <div className="pg-rps-loading-step is-pending">
          <span>○</span>
          {t('preview_loading_step4')}
        </div>
        <div className="pg-rps-loading-step is-pending">
          <span>○</span>
          {t('preview_loading_step5')}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Preview — État RESULT (vrai rapport final)
// ============================================================================
function PreviewResult({ result }) {
  const { t } = useTranslation('scoreRps');

  // Ceinture+bretelles : même si handleCalculate valide la shape en amont, on protège
  // le render contre une donnée partielle qui crasherait silencieusement la page.
  const restaurant = result?.restaurant || {};
  const scores = result?.scores || {};
  const verdict = result?.verdict || {};
  const satisfaction_analysis = result?.satisfaction_analysis;
  const action_plan = result?.action_plan;

  const totalValue = scores?.total?.value ?? 0;
  const [animatedScore, setAnimatedScore] = useState(0);

  // Animation compteur de 0 au score final (inchangée)
  useEffect(() => {
    const target = totalValue;
    const duration = 1200;
    const steps = 30;
    const increment = target / steps;
    let current = 0;
    const interval = setInterval(() => {
      current += increment;
      if (current >= target) {
        setAnimatedScore(target);
        clearInterval(interval);
      } else {
        setAnimatedScore(Math.round(current));
      }
    }, duration / steps);
    return () => clearInterval(interval);
  }, [totalValue]);

  const verdictColor = verdict.color || 'rgba(245,242,235,0.5)';

  return (
    <div className="pg-rps-preview-card">
      <div className="pg-rps-preview-badge is-score">{t('preview_result_badge')}</div>

      <div className="pg-rps-preview-label">{restaurant.name || t('preview_result_default_name')}</div>

      <div className="pg-rps-score" style={{ color: verdictColor }}>
        {animatedScore}<span className="pg-rps-score-max">/100</span>
      </div>

      <div className="pg-rps-verdict-badge" style={{ background: verdictColor + '25', color: verdictColor }}>
        {verdict.emoji || '📊'} {verdict.label || ''}
      </div>

      <div className="pg-rps-verdict-msg">{verdict.message || ''}</div>

      {satisfaction_analysis?.summary && (
        <div className="pg-rps-quote">
          "{satisfaction_analysis.summary}"
          <div className="pg-rps-quote-source">🧠 Analyse IA Claude Haiku</div>
        </div>
      )}

      <PreviewBar label={t('bar_label_note')} value={scores.note?.value ?? 0} max={scores.note?.max ?? 40} detail={`${restaurant.rating ?? '—'}/5`} />
      <PreviewBar label={t('bar_label_satisfaction')} value={scores.satisfaction?.value ?? 0} max={scores.satisfaction?.max ?? 30} detail={t('bar_detail_satisfaction')} />
      <PreviewBar label={t('bar_label_volume')} value={scores.volume?.value ?? 0} max={scores.volume?.max ?? 30} detail={`${restaurant.reviews_count ?? 0} avis`} />

      {action_plan && action_plan.length > 0 && (
        <div className="pg-rps-preview-footer">
          <div className="pg-rps-preview-footer-title">{t('preview_result_action_plan_title')}</div>
          {action_plan.map((a, i) => (
            <div key={i} className="pg-rps-preview-footer-item">
              {a.icon} {a.title}
            </div>
          ))}
        </div>
      )}

      {/* CTA final — inscription */}
      <Link to="/register" className="pg-rps-preview-cta">
        {t('preview_result_cta')}
      </Link>
      <div className="pg-rps-preview-cta-note">
        {t('preview_result_email_sent')}
      </div>
    </div>
  );
}

// ============================================================================
// Barre de progression dans la preview (inchangée logiquement)
// ============================================================================
function PreviewBar({ label, value, max, detail }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="pg-rps-bar-block">
      <div className="pg-rps-bar-header">
        <span className="pg-rps-bar-label">{label}</span>
        <span className="pg-rps-bar-value">
          <strong>{value}</strong>/{max}
          {detail && <span className="pg-rps-bar-detail"> · {detail}</span>}
        </span>
      </div>
      <div className="pg-rps-bar-track">
        <div className="pg-rps-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
